// src/components/signage/CampaignPlayer.js
// Fullscreen screensaver-like campaign player
// Interrupted by mouse click or keyboard press — returns to previous context
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { fetchMediaAsBlob, releaseMediaBlobCache } from '../../services/signageService';
import { useAuth } from '../../context/AuthContext';
import './CampaignPlayer.css';

/**
 * CampaignPlayer - Fullscreen slideshow player
 * @param {Object} props
 * @param {Object} props.campaign - Campaign object with slides array
 * @param {Function} props.onExit - Called when user clicks/presses key to exit
 * @param {boolean} props.loop - Whether to loop (default: true)
 */
function CampaignPlayer({ campaign, onExit, loop = true }) {
    const { user } = useAuth();
    const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
    const [resolvedSlides, setResolvedSlides] = useState([]);
    const timerRef = useRef(null);
    const videoRef = useRef(null);
    const containerRef = useRef(null);

    // ────────────────────────────────────────────────────────────────────────
    // Sequential / depth-N prefetch.
    //
    // Phase 2 (LANE-RPI-VIDEO): keep only currentSlideIndex ± PREFETCH_DEPTH
    // slides hot in memory. Drop the "fetch all 9 slides in parallel" pattern
    // from the previous version because it held every slide's blob in RAM at
    // once — fine for 9 × 1.7 MB images, fatal for a 19 MB campaign video on
    // a 2 GB Pi.
    //
    // The cache lives in signageService._mediaBlobCache (module-level); we ask
    // fetchMediaAsBlob, which is keyed by the server path. Repeated calls for
    // the same slide are no-ops server-side, so it's cheap to call twice.
    //
    // `touchedIndicesRef` is the set of indices whose blobs we've already
    // requested and still want to keep. After we resolve, we keep those and
    // revoke any blob URL for an index no longer in `touchedIndices`.
    // ────────────────────────────────────────────────────────────────────────
    const PREFETCH_DEPTH = 1; // current + next (i.e. depth=2 in memory)

    useEffect(() => {
        if (!campaign?.slides?.length) {
            setResolvedSlides([]);
            return;
        }
        const total = campaign.slides.length;

        // Which slide indices should be warm right now?
        const want = new Set();
        want.add(currentSlideIndex);
        for (let off = 1; off <= PREFETCH_DEPTH; off++) {
            want.add((currentSlideIndex + off) % total);
        }

        let cancelled = false;

        // Fetch all "wanted" indices in parallel — that's only 2 blobs (the
        // current + next). Far cheaper than the old Promise.all across 9+.
        Promise.all(
            Array.from(want).map(async (i) => ({
                ...campaign.slides[i],
                _idx: i,
                src: await fetchMediaAsBlob(playableSrc(campaign.slides[i])),
            }))
        ).then(resolved => {
            if (cancelled) return;

            // Merge into the existing resolvedSlides array (which may already
            // have other indices from before this slideIndex change).
            setResolvedSlides(prev => {
                const byIdx = new Map(prev.map(s => [s._idx ?? campaign.slides.findIndex(x => x.id === s.id), s]));
                // Drop any index no longer wanted.
                for (const k of Array.from(byIdx.keys())) {
                    if (!want.has(k)) byIdx.delete(k);
                }
                // Merge this batch.
                for (const s of resolved) byIdx.set(s._idx, s);
                // Return in campaign order.
                const out = [];
                for (let i = 0; i < total; i++) {
                    if (byIdx.has(i)) out.push(byIdx.get(i));
                }
                return out;
            });
        });

        return () => { cancelled = true; };
    }, [campaign, currentSlideIndex]);

    // Release all cached blob URLs when the player unmounts so the browser
    // can reclaim memory. Any subsequent play session will re-fetch from the
    // network (a cold cache is far preferable to an ever-growing blob store).
    useEffect(() => {
        return () => { releaseMediaBlobCache(); };
    }, []);

    const slides = resolvedSlides;
    const totalSlides = campaign?.slides?.length ?? 0;

    // For video slides, prefer the transcoded URL when the API has marked it
    // 'ready'. The kiosk's Chromium on RPi 5 hardware-decodes H.264/AAC and
    // gets served 206 Partial Content — VP8/WebM fallbacks are software-decoded
    // and behave badly on CrOS. Images are unaffected (no transcode).
    const playableSrc = (slide) =>
        (slide?.type === 'video' && slide?.transcodedUrl) ? slide.transcodedUrl : slide?.src;

    // resolvedSlides is a compacted array containing only the warm window;
    // its array position does NOT equal the campaign index.
    // Always look up by _idx to get the correct slide.
    const findSlide = (idx) => slides.find(s => s._idx === idx);

    // Advance to next slide. Wraps against `campaign.slides.length` (the
    // authoritative count) rather than `resolvedSlides.length` (which only
    // contains the prefetched warm subset).
    const goToNextSlide = useCallback(() => {
        setCurrentSlideIndex(prev => {
            if (totalSlides === 0) return 0;
            const next = prev + 1;
            if (next >= totalSlides) {
                return loop ? 0 : prev;
            }
            return next;
        });
    }, [totalSlides, loop]);

    // Set timer for current slide
    useEffect(() => {
        if (totalSlides === 0) return;
        if (currentSlideIndex >= totalSlides) return;

        const currentSlide = findSlide(currentSlideIndex);
        if (!currentSlide) return;

        // For videos, wait for video to end instead of using timer
        if (currentSlide.type === 'video') {
            // Video onEnded will handle advancing
            return;
        }

        // For images, use the defined duration
        const duration = (currentSlide.duration || 5) * 1000;
        timerRef.current = setTimeout(goToNextSlide, duration);

        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, [currentSlideIndex, slides, goToNextSlide, totalSlides]);

    // Handle video ended
    const handleVideoEnded = useCallback(() => {
        goToNextSlide();
    }, [goToNextSlide]);

    // Exit on click or keypress
    useEffect(() => {
        const handleInteraction = (e) => {
            // Prevent default to avoid any unwanted behavior
            e.preventDefault();
            if (onExit) onExit();
        };

        const handleKeyDown = (e) => {
            if (onExit) onExit();
        };

        document.addEventListener('mousedown', handleInteraction);
        document.addEventListener('keydown', handleKeyDown);
        document.addEventListener('touchstart', handleInteraction);

        return () => {
            document.removeEventListener('mousedown', handleInteraction);
            document.removeEventListener('keydown', handleKeyDown);
            document.removeEventListener('touchstart', handleInteraction);
        };
    }, [onExit]);

    // Hide cursor after 2s of no movement
    const [showCursor, setShowCursor] = useState(false);
    const cursorTimerRef = useRef(null);

    useEffect(() => {
        const handleMouseMove = () => {
            setShowCursor(true);
            if (cursorTimerRef.current) clearTimeout(cursorTimerRef.current);
            cursorTimerRef.current = setTimeout(() => setShowCursor(false), 2000);
        };

        document.addEventListener('mousemove', handleMouseMove);
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            if (cursorTimerRef.current) clearTimeout(cursorTimerRef.current);
        };
    }, []);

    // Show loading while pre-fetching media
    if (campaign?.slides?.length && resolvedSlides.length === 0) {
        return (
            <div className="campaign-player-overlay">
                <div style={{ color: '#fff', fontSize: '1.2rem', textAlign: 'center' }}>
                    <p>Cargando...</p>
                </div>
            </div>
        );
    }

    if (!campaign || slides.length === 0) {
        return (
            <div className="campaign-player-overlay" onClick={onExit}>
                <div style={{ color: '#fff', fontSize: '1.5rem', textAlign: 'center' }}>
                    <p>No hay slides en esta campaña</p>
                    <p style={{ fontSize: '0.9rem', opacity: 0.6 }}>Click o tecla para volver</p>
                </div>
            </div>
        );
    }

    const currentSlide = findSlide(currentSlideIndex);
    const orientation = campaign.orientation || 'landscape';

    // Current slide not yet in the warm window (prefetch in flight) — wait.
    if (!currentSlide) {
        return (
            <div className="campaign-player-overlay">
                <div style={{ color: '#fff', fontSize: '1.2rem', textAlign: 'center' }}>
                    <p>Cargando...</p>
                </div>
            </div>
        );
    }

    return (
        <div
            ref={containerRef}
            className={`campaign-player-overlay ${showCursor ? 'show-cursor' : ''}`}
        >
            <div
                key={currentSlideIndex}
                className={`campaign-player-slide orientation-${orientation}`}
            >
                {currentSlide.type === 'video' ? (
                    <video
                        ref={videoRef}
                        src={currentSlide.src}
                        autoPlay
                        muted
                        onEnded={handleVideoEnded}
                        style={{ pointerEvents: 'none' }}
                    />
                ) : (
                    <img
                        src={currentSlide.src}
                        alt={currentSlide.filename || `Slide ${currentSlideIndex + 1}`}
                        draggable={false}
                    />
                )}
            </div>

            {/* Branding watermark */}
            <div className="campaign-player-branding">
                <span className="campaign-player-branding-line">Realizado por Proyectos Horizontales</span>
                <span className="campaign-player-branding-url">proyectoshorizontales.com</span>
            </div>

            {/* CTA watermark – only for totem-enabled orgs */}
            {user?.totemEnabled && (
                <div className="campaign-player-cta">
                    <span className="campaign-player-cta-line">Presione una tecla</span>
                    <span className="campaign-player-cta-line">para buscar productos</span>
                </div>
            )}
        </div>
    );
}

export default CampaignPlayer;
